import React from 'react';
import '../../styles/shared.css';
import '../../styles/formats.css';
import { generateAdUnitPath } from '../../utils/formatUtils';
import { GPT_FORMATS, getGptFormatIcon } from '../../constants/formatConstants';

interface FormatsTabProps {
    handleForm: any;
}

const GptFormatsTab: React.FC<FormatsTabProps> = ({ handleForm }) => {
    // Obter placements GPT do backend
    const getGptPlacements = () => {
        const placements = handleForm.values.placements || [];
        return placements.filter((p: any) => p.provider === 'gpt');
    };

    const handleGenerateAdUnitPath = (formatName: string) => {
        return generateAdUnitPath(
            'gpt',
            formatName,
            undefined,
            handleForm.values.managerType,
            handleForm.values.networkId,
            handleForm.values.domain
        );
    };

    // Atualizar placement no array
    const updatePlacementField = (placementId: string, field: string, value: any) => {
        const updatedPlacements = (handleForm.values.placements || []).map((p: any) => {
            if (p.id === placementId || (p.name === placementId && p.provider === 'gpt')) {
                return { ...p, [field]: value };
            }
            return p;
        });
        handleForm.setFieldValue('placements', updatedPlacements);
    };

    // Toggle habilitado/desabilitado
    const togglePlacementEnabled = (placementId: string) => {
        const placement = (handleForm.values.placements || []).find((p: any) =>
            p.id === placementId || (p.name === placementId && p.provider === 'gpt')
        );
        if (placement) {
            updatePlacementField(placementId, 'enabled', !placement.enabled);
        }
    };

    // Verificar se há múltiplos anchors ativos (para validação de formatos single position)
    const hasActiveAnchor = () => {
        const anchorTypes = ['TOP_ANCHOR', 'BOTTOM_ANCHOR', 'AUTO_ANCHOR'];
        const placements = handleForm.values.placements || [];
        return placements.filter((p: any) => anchorTypes.includes(p.name) && p.enabled).length > 1;
    };

    // Renderizar input de bidders
    const renderBiddersInput = (placement: any) => {
        const baseId = placement.id || placement.name;
        const fieldId = `bidders-${baseId}`;
        return (
            <div className="form-group">
                <label htmlFor={fieldId} className="form-label">
                    Bidders Configuration (JSON)<span className="text-muted small d-block">Configuração dos bidders para Prebid</span>
                </label>
                <textarea
                    id={fieldId}
                    name={fieldId}
                    className="form-control json-textarea"
                    rows={6}
                    placeholder={`[
  {
    "bidder": "appnexus",
    "params": {
      "placementId": 13144370
    }
  },
  {
    "bidder": "rubicon",
    "params": {
      "accountId": "12345",
      "siteId": "67890",
      "zoneId": "13579"
    }
  }
]`}
                    value={placement?.bids ? JSON.stringify(placement.bids, null, 2) : ''}
                    onChange={(e) => updatePlacementField(baseId, 'bids', e.target.value)}
                />
                <div className="json-hint">
                    💡 Formato JSON válido necessário
                </div>
            </div>
        );
    };

    // Renderizar configurações específicas do Rewarded Format
    const renderRewardedConfig = (placement: any) => {
        const baseId = placement.id || placement.name;

        const rewardedTypeOptions = [
            { value: 'premium_article', label: 'Artigo Premium' },
            { value: 'paywall_removal', label: 'Remoção de Paywall' },
            { value: 'download', label: 'Download' },
            { value: 'rewarded', label: 'Recompensa Geral' }
        ];

        const durationOptions = [
            { value: 0, label: 'Sempre exibir' },
            { value: 5, label: '5 Minutos' },
            { value: 10, label: '10 Minutos' },
            { value: 15, label: '15 Minutos' },
            { value: 30, label: '30 Minutos' },
            { value: 45, label: '45 Minutos' },
            { value: 1, label: '1 Hora' },
            { value: 2, label: '2 Horas' },
            { value: 3, label: '3 Horas' },
            { value: 6, label: '6 Horas' },
            { value: 8, label: '8 Horas' },
            { value: 12, label: '12 Horas' },
            { value: 24, label: '1 Dia' },
        ];

        const shouldShowDuration = placement.rewardedType === 'premium_article' || placement.rewardedType === 'paywall_removal';

        return (
            <div className="rewarded-config">
                <div className="config-divider"></div>
                <div className="form-section">
                    <div className="form-section-title">
                        🎁 Configurações de Recompensa
                    </div>

                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <div className="form-group">
                                <label htmlFor={`rewardedType-${baseId}`} className="form-label">
                                    Tipo de Recompensa<span className="required">*</span>
                                    <span className="text-muted small d-block">Selecione o tipo de benefício oferecido</span>
                                </label>
                                <select
                                    id={`rewardedType-${baseId}`}
                                    className="form-select"
                                    value={placement.rewardedType || ''}
                                    onChange={(e) => updatePlacementField(baseId, 'rewardedType', e.target.value)}
                                >
                                    <option value="">Selecione o tipo...</option>
                                    {rewardedTypeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        {shouldShowDuration && (
                            <div className="col-12 col-md-6">
                                <div className="form-group">
                                    <label htmlFor={`accessDuration-${baseId}`} className="form-label">
                                        Duração do Acesso<span className="required">*</span>
                                        <span className="text-muted small d-block">Por quanto tempo o usuário terá acesso</span>
                                    </label>
                                    <select
                                        id={`accessDuration-${baseId}`}
                                        className="form-control"
                                        value={placement.rewardedConfig?.accessDuration || ''}
                                        onChange={(e) => {
                                            const currentConfig = placement.rewardedConfig || {};
                                            updatePlacementField(baseId, 'rewardedConfig', {
                                                ...currentConfig,
                                                accessDuration: parseInt(e.target.value)
                                            });
                                        }}
                                    >
                                        <option value="">Selecione a duração...</option>
                                        {durationOptions.map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // Renderizar configuração de formato
    const renderFormatConfig = (placement: any) => {
        if (!placement.enabled) return null;

        const baseId = placement.id || placement.name;
        const hasPositions = placement.positions && placement.positions.length > 0;
        const isRewardedFormat = placement.name === 'REWARDED' && placement.provider === 'gpt';
        const hideRefresh = (placement.name === 'INTERSTITIAL' || placement.name === 'REWARDED') && placement.provider === 'gpt';

        if (hasPositions) {
            // Formato com múltiplas posições
            return (
                <div className="format-positions">
                    {placement.positions.map((pos: any) => {
                        const isEnabled = placement.enabled && placement.position === pos.value;

                        return (
                            <div key={`${baseId}-${pos.value}`} className={`position-card ${isEnabled ? 'enabled' : 'disabled'}`}>
                                <div className="position-header">
                                    <div className="position-info">
                                        <h5 className="position-name">{pos.label}</h5>
                                    </div>

                                    <div className="position-toggle">
                                        <label className="toggle-switch" onClick={(e) => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                checked={isEnabled}
                                                onChange={(e) => {
                                                    const newPosition = e.target.checked ? pos.value : '';
                                                    updatePlacementField(baseId, 'position', newPosition);
                                                    updatePlacementField(baseId, 'enabled', e.target.checked);
                                                }}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className="toggle-label">
                                            {isEnabled ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>
                                </div>

                                {isEnabled && (
                                    <div className="position-config">
                                        <div className="row g-3">
                                            <div className="col-12 col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Ad Unit Path
                                                        <span className="required">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        placeholder={handleGenerateAdUnitPath(pos.value)}
                                                        value={placement?.adUnitPath || handleGenerateAdUnitPath(pos.value)}
                                                        onChange={(e) => updatePlacementField(baseId, 'adUnitPath', e.target.value)}
                                                    />
                                                    <div className="field-hint">
                                                        💡 Path gerado automaticamente baseado nas configurações
                                                    </div>
                                                </div>
                                            </div>

                                            {!hideRefresh && (
                                            <div className="col-12 col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">
                                                        Tempo de recarregamento (segundos)
                                                        <span className="text-muted small d-block">0 = sem refresh</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        min="0"
                                                        value={placement?.refresh || 30}
                                                        onChange={(e) => updatePlacementField(baseId, 'refresh', parseInt(e.target.value) || 0)}
                                                    />
                                                </div>
                                            </div>
                                            )}
                                        </div>

                                        {/* Show bidders configuration if prebid powerup is enabled */}
                                        {handleForm.values.powerUps?.find((pu: any) => pu.name === 'prebid')?.enabled && renderBiddersInput(placement)}

                                        {/* Show rewarded configuration if this is a rewarded format */}
                                        {placement.name === 'REWARDED' && placement.provider === 'gpt' && renderRewardedConfig(placement)}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {placement.singlePosition && hasActiveAnchor() && (
                        <div className="format-warning">
                            <div className="warning-content">
                                <span className="warning-icon">ℹ️</span>
                                <div className="warning-text">
                                    <strong>Informação:</strong> Este formato permite apenas uma posição ativa por vez.
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            );
        } else {
            // Formato simples sem posições múltiplas
            return (
                <div className="format-config">
                    <div className="config-divider"></div>

                    <div className="row g-3">
                        <div className="col-12 col-md-6">
                            <div className="form-group">
                                <label className="form-label">
                                    Ad Unit Path
                                    <span className="required">*</span>
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder={handleGenerateAdUnitPath(placement.name)}
                                    value={placement?.adUnitPath || handleGenerateAdUnitPath(placement.name)}
                                    onChange={(e) => updatePlacementField(baseId, 'adUnitPath', e.target.value)}
                                />
                                <div className="field-hint">
                                    💡 Path gerado automaticamente baseado nas configurações
                                </div>
                            </div>
                        </div>

                        {!hideRefresh && (
                        <div className="col-12 col-md-6">
                            <div className="form-group">
                                <label className="form-label">
                                    Tempo de recarregamento (segundos)
                                    <span className="help-text">0 = sem refresh</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    value={placement?.refresh || 30}
                                    onChange={(e) => updatePlacementField(baseId, 'refresh', parseInt(e.target.value) || 0)}
                                />
                            </div>
                        </div>
                        )}
                    </div>

                    {/* Show bidders configuration if prebid powerup is enabled */}
                    {handleForm.values.powerUps?.find((pu: any) => pu.name === 'prebid')?.enabled && renderBiddersInput(placement)}

                    {/* Show rewarded configuration if this is a rewarded format */}
                    {placement.name === 'REWARDED' && placement.provider === 'gpt' && renderRewardedConfig(placement)}
                </div>
            );
        }
    };

    const gptPlacements = getGptPlacements();
    const totalFormats = gptPlacements.length;
    const activeFormats = gptPlacements.filter((p: any) => p.enabled).length;

    return (
        <div className="content-section">
            <div className="d-flex justify-content-between align-items-end mb-4 pb-3 border-bottom">
                <div className="flex-grow-1">
                    <h3 className="section-title mb-2">📱 Formatos GPT</h3>
                    <p className="text-muted mb-0 small">
                        Configure os formatos automáticos do Google Ad Manager. Estes formatos são gerenciados
                        diretamente pelo Google e não requerem configurações de seletor CSS.
                    </p>
                </div>
                <div className="d-flex gap-4 ms-4">
                    <div className="text-center">
                        <div className="fs-3 fw-bold text-primary lh-1">{totalFormats}</div>
                        <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Formatos</small>
                    </div>
                    <div className="text-center">
                        <div className="fs-3 fw-bold text-success lh-1">{activeFormats}</div>
                        <small className="text-muted text-uppercase fw-medium" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Ativos</small>
                    </div>
                </div>
            </div>

            <div className="formats-grid">
                {gptPlacements.map((placement: any) => {
                    const isEnabled = placement.enabled;
                    const hasPositions = placement.positions && placement.positions.length > 0;
                    const baseId = placement.id || placement.name;

                    return (
                        <div
                            key={baseId}
                            className={`format-card ${isEnabled ? 'enabled' : 'disabled'} ${hasPositions ? 'has-positions' : ''} gpt-format`}
                        >
                            <div className="format-header">
                                <div className="format-info">
                                    <div className="format-icon">{getGptFormatIcon(placement.name)}</div>
                                    <div className="format-details">
                                        <h4 className="format-name">
                                            {placement.displayName ||
                                                GPT_FORMATS[
                                                placement.name as keyof typeof GPT_FORMATS
                                                ] ||
                                                placement.name}
                                        </h4>
                                        <p className="format-description">
                                            {placement.description || `Formato ${GPT_FORMATS[
                                                placement.name as keyof typeof GPT_FORMATS
                                            ]?.toLowerCase() || placement.name}`}
                                        </p>
                                        {placement.position && (
                                            <span className="format-position">Posição: {placement.position}</span>
                                        )}
                                    </div>
                                </div>

                                {!hasPositions && (
                                    <div className="format-toggle">
                                        <label
                                            className="toggle-switch"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={isEnabled}
                                                onChange={() => togglePlacementEnabled(baseId)}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className="toggle-label">
                                            {isEnabled ? 'Ativo' : 'Inativo'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {renderFormatConfig(placement)}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default GptFormatsTab;